import { useProfile } from "@shared/hooks/useProfile";
import AuthenticatedLayout from "@shared/layouts/AuthenticatedLayout";
import { UserProfile } from "@shared/types/profile.types";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import MessageInput from "../components/MessageInput";
import MessageList from "../components/MessageList";
import MessagesHeader from "../components/MessagesHeader";
import MessagesSidebar from "../components/MessagesSidebar";
import {
  ConversationsProvider,
  useConversations,
} from "../context/ConversationsContext";
import { useMessages } from "../hooks/useMessages";

const MessagesContent: React.FC = () => {
  const [msgSidebarOpen, setMsgSidebarOpen] = useState(true);
  const [selectedConversation, setSelectedConversation] = useState<
    string | null
  >(null);
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);

  const { user } = useProfile();
  const { conversations, userProfiles } = useConversations();

  const {
    messages,
    loading: messagesLoading,
    sendMessage,
    deleteMessage,
    updateTypingStatus,
    hasMore,
    loadMoreMessages: loadMore,
    isTyping,
  } = useMessages(selectedConversation || "", selectedUser);

  const handleDeleteMessage = async (messageId: string) => {
    try {
      await deleteMessage(messageId);
      toast.success("Message deleted successfully");
    } catch (error) {
      console.error("Error deleting message:", error);
      toast.error("Failed to delete message. Please try again.");
      throw error;
    }
  };

  // Update selected user when conversation changes
  useEffect(() => {
    if (selectedConversation && conversations.length > 0) {
      const conversation = conversations.find(
        (conv) => conv.id === selectedConversation
      );
      if (conversation) {
        const otherUserId = conversation.participants.find(
          (id) => id !== user?.id
        );
        if (otherUserId) {
          setSelectedUser(userProfiles[otherUserId]);
        }
      }
    } else {
      setSelectedUser(null);
    }
  }, [selectedConversation, conversations, userProfiles, user?.id]);

  return (
    <AuthenticatedLayout className="md:h-auto h-full overflow-hidden">
      <div className="relative flex h-full">
        {/* Messages sidebar */}
        <MessagesSidebar
          msgSidebarOpen={msgSidebarOpen}
          setMsgSidebarOpen={setMsgSidebarOpen}
          selectedConversation={selectedConversation}
          setSelectedConversation={setSelectedConversation}
        />
        {/* Messages body */}
        <div
          className={`grow flex flex-col md:translate-x-0 transition-transform duration-300 ease-in-out overflow-hidden ${
            msgSidebarOpen ? "translate-x-1/3" : "translate-x-0"
          }`}
        >
          {selectedConversation ? (
            <>
              <MessagesHeader
                msgSidebarOpen={msgSidebarOpen}
                setMsgSidebarOpen={setMsgSidebarOpen}
                selectedUser={selectedUser}
                isTyping={isTyping}
              />
              <MessageList
                messages={messages}
                loading={messagesLoading}
                hasMore={hasMore}
                onLoadMore={loadMore}
                onDeleteMessage={handleDeleteMessage}
                user={user}
                selectedUser={selectedUser}
              />
              <MessageInput
                onSendMessage={(content, type, mediaUrl) =>
                  sendMessage(content, type, mediaUrl)
                }
                updateTypingStatus={updateTypingStatus}
              />
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <p className="text-slate-500 dark:text-slate-400">
                Select a conversation to start messaging
              </p>
            </div>
          )}
        </div>
      </div>
    </AuthenticatedLayout>
  );
};

const MessagesPage: React.FC = () => {
  return (
    <ConversationsProvider>
      <MessagesContent />
    </ConversationsProvider>
  );
};

export default MessagesPage;
